import React, { PropsWithChildren, useCallback, useState, useEffect, useRef } from 'react'
import { ScrollView, StyleSheet } from 'react-native'
import { DNABox } from '@alucio/lux-ui'

import { DNANavigationMenu } from 'src/components/DNA'
import DNAAppHeader from 'src/components/DNA/Header/DNAAppHeader'
import DNAFooter from 'src/components/Footer/Footer'

import { useAppSettings } from 'src/state/context/AppSettings'
import InfoPanel from 'src/components/InfoPanel/InfoPanel'
import { RootState } from 'src/state/redux'
import { useSelector } from 'react-redux'
import useCurrentPage from '../hooks/useCurrentPage'
import { useIPadOverscroll } from 'src/hooks/useIPadOverscroll'
import { useBreakpointValue } from 'src/hooks/useBreakpointValue/useBreakpointValue'
import DocumentSearchProvider, { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2'
import { SearchTextContextProvider } from 'src/components/Header/SearchInput/SearchInput';
import AISearchManager from 'src/components/Header/SearchInput/aiSearch/AISearchManager'
import AISearchFloatingProgress from 'src/components/Header/SearchInput/aiSearch/AISearchFloatingProgress'

type ScrollableNode = ScrollView & {
  children?: Array<{ scrollTo?: (x: number, y: number) => void }>
}

// Routes that should skip horizontal padding
const ROUTES_WITHOUT_PADDING = ['meeting-presentation'];

const S = StyleSheet.create({
  lowerZIndex: {
    zIndex: -1,
  },

  container: {
    flex: 1,
    justifyContent: 'space-between',
    zIndex: -1,
    overflow: 'scroll',
  },
  containerDisabledScroll: {
    flex: 1,
    justifyContent: 'space-between',
    zIndex: -1,
  },
  contentContainer: {
    flex: 1,
    zIndex: -1,
  },
})

/**
 * This component isolates the consumption of useDocumentSearchV2Context.
 * By using a separate "renderless" component, we ensure that changes to searchText
 * only trigger a re-render of this component and not the entire AppContainerUI2.
 * This is necessary because calling the hook directly in AppContainerUI2 would
 * make it a context consumer, forcing it to re-render.
 */
const SearchScrollRestorer = ({ scrollViewRef }: { scrollViewRef: React.RefObject<ScrollView> }) => {
  const { searchText } = useDocumentSearchV2Context()

  useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: false })
    const scrollViewWithChildren = scrollViewRef.current as ScrollableNode
    scrollViewWithChildren?.children?.[0]?.scrollTo?.(0, 0)
  }, [searchText, scrollViewRef])

  return null
}

const infoPanelSelector = (state: RootState) => state.infoPanel

const AppContainerUI2: React.FC<PropsWithChildren> = ({ children }) => {
  const { activeItemORM } = useSelector(infoPanelSelector)
  const { deviceMode } = useAppSettings()

  const currentPage = useCurrentPage();
  const { enableHorizontalScroll, disableGlobalScroll } = currentPage?.configOptions?.[deviceMode] ?? {}
  const [width, setWidth] = useState(0);
  const horizontalPadding = useBreakpointValue('responsivePadding');
  const shouldSkipPadding = currentPage?.UID && ROUTES_WITHOUT_PADDING.includes(currentPage.UID);
  const scrollViewRef = useRef<ScrollView>(null)

  const onLayout = useCallback(
    (event) => {
      const { width = 0 } = event.nativeEvent.layout
      setWidth(width)
    },
    [],
  )

  return (
    <DNABox fill appearance="rowReverse">
      <SearchScrollRestorer scrollViewRef={scrollViewRef} />
      <DNABox fill appearance="col">
        <DNAAppHeader />
        <DNABox fill>
          <ScrollView
            ref={scrollViewRef}
            contentContainerStyle={[
              disableGlobalScroll
                ? S.containerDisabledScroll
                : S.container,
              !shouldSkipPadding && { paddingHorizontal: horizontalPadding },
            ]}
            testID="AppContainerScroll"
          >
            {enableHorizontalScroll
              ? <>
                <DNABox
                  testID="enableHorizontalScrollContainer"
                  appearance="row"
                  onLayout={onLayout}
                >
                  {children}
                </DNABox>
                <DNAFooter width={width} />
              </>
              : <>
                {children}
                <DNAFooter />
              </>
            }
          </ScrollView>
          <InfoPanel
            variant="Folder"
            itemORM={activeItemORM}
          />
        </DNABox>
      </DNABox>
      <DNANavigationMenu/>
    </DNABox>
  )
}

const AppContainer: React.FC<PropsWithChildren> = (props) => {
  useIPadOverscroll();

  return (
    <DocumentSearchProvider>
      <SearchTextContextProvider>
        <AISearchManager />
        <AppContainerUI2 {...props}/>
        <AISearchFloatingProgress />
      </SearchTextContextProvider>
    </DocumentSearchProvider>
  )
}

export default AppContainer
